import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaAward, FaTimes, FaHandshake, FaCalendarCheck, FaUsers, FaClock, FaUserFriends, FaMoneyBillWave, FaCheckCircle, FaCheck, FaMapMarkerAlt, FaPlus } from 'react-icons/fa';
import { CiEdit } from 'react-icons/ci';

interface PointRule {
  id: number;
  type: string;
  data: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const DEFAULT_RULE_METADATA: Record<string, { label: string; icon: any; category: string; desc: string }> = {
  R2RHost: { label: 'R2R Meeting Host', icon: FaHandshake, category: 'R2R Meeting', desc: 'Points awarded to member hosting an R2R meeting' },
  R2RGuest: { label: 'R2R Meeting Visitor / Guest', icon: FaHandshake, category: 'R2R Meeting', desc: 'Points awarded to visitor member attending R2R' },
  Attendance: { label: 'Meeting Attendance (Present)', icon: FaCalendarCheck, category: 'Attendance', desc: 'Points awarded for full meeting attendance' },
  Late: { label: 'Meeting Attendance (Late)', icon: FaClock, category: 'Attendance', desc: 'Points awarded for late arrival at meeting' },
  Substitute: { label: 'Meeting Attendance (Substitute)', icon: FaUserFriends, category: 'Attendance', desc: 'Points awarded when sending a substitute' },
  Reference: { label: 'Business Referral Passed', icon: FaMoneyBillWave, category: 'Business', desc: 'Points awarded for passing a business reference' },
  Visitor: { label: 'Visitor / Guest Invited', icon: FaUsers, category: 'Invitations', desc: 'Points awarded for bringing a guest/visitor to meeting' },
  FieldVisit: { label: 'Field Visit / Factory Visit', icon: FaMapMarkerAlt, category: 'Field Visit', desc: 'Points awarded to member participating in or hosting a Field Visit' },
  FIELD_VISIT: { label: 'Field Visit / Factory Visit', icon: FaMapMarkerAlt, category: 'Field Visit', desc: 'Points awarded to member participating in or hosting a Field Visit' },
  Leave: { label: 'Meeting Leave Approved', icon: FaCalendarCheck, category: 'Attendance', desc: 'Approved leave status (no points credited)' },
  Absent: { label: 'Meeting Absent', icon: FaCalendarCheck, category: 'Attendance', desc: 'Absent status (0 points credited)' }
};

const PointRulesReferenceModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [rules, setRules] = useState<PointRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [editPointsVal, setEditPointsVal] = useState<number>(0);
  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchRules();
    }
  }, [isOpen]);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await axios.get('https://rmbbackend.rmbjaipur.com/auth/point');
      let dataList: PointRule[] = [];
      if (res.data?.data && Array.isArray(res.data.data)) dataList = res.data.data;
      else if (Array.isArray(res.data)) dataList = res.data;

      // Ensure FieldVisit rule is present in list
      const hasFieldVisit = dataList.some(r => r.type.toLowerCase() === 'fieldvisit' || r.type.toLowerCase() === 'field_visit');
      if (!hasFieldVisit) {
        try {
          const createRes = await axios.post('https://rmbbackend.rmbjaipur.com/auth/point', {
            type: 'FieldVisit',
            data: 10
          });
          if (createRes.data?.recordId) {
            dataList.push({ id: createRes.data.recordId, type: 'FieldVisit', data: 10 });
          }
        } catch (e) {
          dataList.push({ id: 9999, type: 'FieldVisit', data: 10 });
        }
      }

      setRules(dataList);
    } catch (err) {
      console.error('Error fetching point rules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (rule: PointRule) => {
    setEditingRuleId(rule.id);
    setEditPointsVal(rule.data);
    setStatusMsg(null);
  };

  const handleSaveEdit = async (rule: PointRule) => {
    setSaving(true);
    setStatusMsg(null);
    try {
      await axios.put(`https://rmbbackend.rmbjaipur.com/auth/point/${rule.id}`, {
        type: rule.type,
        data: Number(editPointsVal)
      });
      setStatusMsg(`Updated ${rule.type} rule points to +${editPointsVal} pts successfully!`);
      setEditingRuleId(null);
      fetchRules();
    } catch (err) {
      console.error('Error updating point rule:', err);
      setStatusMsg('Failed to update point rule.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-xs flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[#1E2B45] text-slate-100 rounded-2xl border border-[#2A3B5C] shadow-2xl w-full max-w-4xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-[#0A2451] p-5 border-b border-[#2A3B5C] flex justify-between items-center">
          <div>
            <h2 className="text-lg font-black text-white flex items-center gap-2">
              <FaAward className="text-[#FDBC12]" /> Editable Points System Rules Reference Matrix
            </h2>
            <p className="text-xs text-slate-300">Click "Edit Points" on any rule to update point values in real time.</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 bg-slate-800 hover:bg-rose-600 text-white rounded-full flex items-center justify-center transition cursor-pointer"
          >
            <FaTimes />
          </button>
        </div>

        {statusMsg && (
          <div className="mx-5 mt-4 p-3 bg-emerald-950/80 text-emerald-300 border border-emerald-500/40 rounded-xl text-xs font-bold flex items-center gap-2">
            <FaCheckCircle className="text-emerald-400" />
            {statusMsg}
          </div>
        )}

        {/* Body */}
        <div className="p-5 max-h-[75vh] overflow-y-auto space-y-4">
          {loading ? (
            <div className="p-8 text-center text-slate-400 font-bold animate-pulse">Loading point rules reference...</div>
          ) : (
            <div className="border border-[#2A3B5C] rounded-xl overflow-hidden shadow">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-[#0A2451] text-slate-300 font-extrabold uppercase border-b border-[#2A3B5C]">
                    <th className="p-3">Activity / Attendance Type</th>
                    <th className="p-3">Category</th>
                    <th className="p-3">Rule Code</th>
                    <th className="p-3 text-center">Points Awarded</th>
                    <th className="p-3 text-right">Actions / Edit</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2A3B5C] bg-[#131E32]">
                  {rules.length > 0 ? (
                    rules.map((r) => {
                      const meta = DEFAULT_RULE_METADATA[r.type] || {
                        label: r.type,
                        icon: FaAward,
                        category: 'General',
                        desc: 'Point allocation rule'
                      };
                      const IconComp = meta.icon;

                      return (
                        <tr key={r.id} className="hover:bg-[#1E2B45] transition">
                          <td className="p-3 font-bold text-white">
                            <div className="flex items-center gap-2">
                              <span className="p-1.5 bg-[#0A2451] rounded-lg text-[#FDBC12]">
                                <IconComp size={14} />
                              </span>
                              <div>
                                <div>{meta.label}</div>
                                <div className="text-[10px] text-slate-400 font-normal">{meta.desc}</div>
                              </div>
                            </div>
                          </td>

                          <td className="p-3">
                            <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase bg-slate-800 text-slate-300 border border-slate-700">
                              {meta.category}
                            </span>
                          </td>

                          <td className="p-3 font-mono font-bold text-slate-300">
                            {r.type}
                          </td>

                          <td className="p-3 text-center">
                            {editingRuleId === r.id ? (
                              <div className="flex items-center justify-center gap-1">
                                <input
                                  type="number"
                                  value={editPointsVal}
                                  onChange={(e) => setEditPointsVal(Number(e.target.value))}
                                  className="w-16 p-1 border border-[#FDBC12] rounded bg-[#0A2451] text-center text-xs font-black text-[#FDBC12] outline-none"
                                  min="0"
                                  autoFocus
                                />
                                <span className="text-xs font-bold text-slate-300">Pts</span>
                              </div>
                            ) : (
                              <span className="px-3 py-1 rounded-full font-black text-xs bg-amber-950 text-[#FDBC12] border border-amber-500/40">
                                +{r.data} Pts
                              </span>
                            )}
                          </td>

                          <td className="p-3 text-right">
                            {editingRuleId === r.id ? (
                              <div className="flex justify-end gap-1">
                                <button
                                  onClick={() => handleSaveEdit(r)}
                                  disabled={saving}
                                  className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded shadow flex items-center gap-1 cursor-pointer"
                                >
                                  <FaCheck /> Save
                                </button>
                                <button
                                  onClick={() => setEditingRuleId(null)}
                                  className="px-2.5 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 font-bold text-xs rounded cursor-pointer"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleStartEdit(r)}
                                className="px-2.5 py-1 bg-blue-900/60 hover:bg-blue-800 text-blue-200 border border-blue-600/40 font-bold text-xs rounded shadow flex items-center gap-1 ml-auto cursor-pointer transition"
                              >
                                <CiEdit /> Edit Points
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="p-6 text-center text-slate-400 font-bold">No point rules configured.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PointRulesReferenceModal;
